import { Box, Typography } from "@mui/material";
import { setSelectedModule, useAuthSlice } from "../../redux/slice/authSlice";
import { fetchUserProfileById, useUserReducer } from "../../redux/slice/userSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { EditOutlined, PersonOutlined } from "@mui/icons-material";
import Button from "../common/Button";
import { useEffect } from "react";
import { USER_ROLE_ENTITY_NAMES } from "../../constants/applicationConstants";
import { useNavigation } from "../../hooks/useNavigation";
import { useModalStyles } from "../common/useModalStyles";

const Profile = () => {
  const { userInfo } = useAuthSlice();
  const { userObject } = useUserReducer();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const { tokens: mTokens, isDark, primaryBtnSx } = useModalStyles();

  const isProviderUser = userInfo.entityName === USER_ROLE_ENTITY_NAMES.PROVIDER;
  const hasAddress =
    userObject?.streetAddress !== null &&
    userObject?.city !== null &&
    userObject?.state !== null &&
    userObject?.zipCode !== null;

  useEffect(() => {
    dispatch(fetchUserProfileById(parseInt(String(userInfo.userId))));
  }, []);

  const handleActionEdit = () => {
    const { userName, userId, firstName, lastName } = userInfo;
    navigate("/user_create", {
      state: { user: { userName, userId, firstName, lastName } },
    });
    dispatch(setSelectedModule("User"));
  };

  const fields = [
    {
      label: "Full Name",
      value: `${userObject?.firstName || ""} ${userObject?.middleName ? ` ${userObject.middleName} ` : ""}${userObject?.lastName || ""}`,
    },
    { label: "Phone", value: userObject?.phone },
    { label: "Email", value: userObject?.email },
    ...(isProviderUser && hasAddress
      ? [
          {
            label: "Address",
            value: [
              userObject?.streetAddress,
              userObject?.unitNumber && userObject.unitNumber !== "N/A" ? userObject.unitNumber : "",
              userObject?.city,
              userObject?.state,
              userObject?.zipCode,
            ]
              .filter(Boolean)
              .join(", "),
          },
        ]
      : []),
  ];

  return (
    <Box sx={{ width: "100%" }}>
      {/* No nested card here — this content already lives inside the sidebar
          shell card, so a second bordered box just doubled up the framing. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.4 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: `${mTokens.accent}14`,
              color: mTokens.accent,
              flexShrink: 0,
            }}
          >
            <PersonOutlined fontSize="small" />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: mTokens.textPrimary, lineHeight: 1.2 }}>
              Personal Details
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: mTokens.textSecondary, mt: 0.15 }}>
              Your account's basic information
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<EditOutlined />}
          variant="contained"
          removeUpperCase
          onClick={() => handleActionEdit()}
          sx={{ ...primaryBtnSx, minWidth: 160 }}
        >
          Edit Profile
        </Button>
      </Box>

      <Box
        sx={{
          borderRadius: "14px",
          border: `1px solid ${mTokens.border}`,
          overflow: "hidden",
        }}
      >
        {fields.map((field, index) => (
          <Box
            key={field.label}
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", sm: "center" },
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: { xs: 0.4, sm: 2 },
              px: 2.4,
              py: 1.8,
              borderBottom: index < fields.length - 1 ? `1px solid ${mTokens.border}` : "none",
              backgroundColor: index % 2 === 0 ? "transparent" : isDark ? "rgba(255,255,255,0.015)" : "rgba(15,23,42,0.012)",
            }}
          >
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: mTokens.textSecondary, minWidth: 140 }}>
              {field.label}
            </Typography>
            <Typography
              sx={{ fontSize: "0.94rem", fontWeight: 700, color: mTokens.textPrimary, textAlign: { xs: "left", sm: "right" } }}
            >
              {field.value || "—"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default Profile;
